import React, { useState, useEffect } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Row,
  Col,
  PreviewAltCard,
  Icon,
  Button,
  RSelect,
} from "../../../components/Component";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import { Spinner, Badge, Input } from "reactstrap";
import {
  useIntelligentReportGeneration,
  IntelligentReportSynthesisContent,
} from "../../../components/reports/intelligentReportShared";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const CustomReportsPage = () => {
  const [statsLoading, setStatsLoading] = useState(false);
  const [showDetailedStats, setShowDetailedStats] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [sites, setSites] = useState([]);
  /** Un seul site par rapport (obligatoire pour PDF, stats et synthèse IA). */
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedSeverities, setSelectedSeverities] = useState([]);
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeDetails, setIncludeDetails] = useState(true);
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [endDate, setEndDate] = useState(new Date());
  const [downloading, setDownloading] = useState(false);

  const typeOptions = [
    { value: "positive", label: "Positive" },
    { value: "dangerous", label: "Dangereuse" },
    { value: "near_miss", label: "Presque accident" },
    { value: "non_conformity", label: "Non-conformité" },
  ];

  const statusOptions = [
    { value: "draft", label: "Brouillon" },
    { value: "submitted", label: "Soumis" },
    { value: "received", label: "Reçue" },
    { value: "in_analysis", label: "En analyse" },
    { value: "corrective_action_requested", label: "Action requise" },
    { value: "action_completed", label: "Action réalisée" },
    { value: "verified", label: "Vérifiée" },
    { value: "in_action", label: "En action" },
    { value: "closed", label: "Clôturé" },
    { value: "rejected", label: "Rejeté" },
  ];

  const severityOptions = [
    { value: "low", label: "Faible" },
    { value: "medium", label: "Moyenne" },
    { value: "high", label: "Élevée" },
  ];

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    setShowDetailedStats(false);
    setReportData(null);
  }, [
    startDate,
    endDate,
    includeCharts,
    includeDetails,
    selectedSiteId,
    JSON.stringify(selectedTypes),
    JSON.stringify(selectedStatuses),
    JSON.stringify(selectedSeverities),
  ]);

  const loadSites = async () => {
    try {
      const response = await api.getSites();
      const sitesList = Array.isArray(response) ? response : response.data || [];
      setSites(sitesList);
    } catch (error) {
      console.error("Erreur lors du chargement des sites:", error);
    }
  };

  const hasSelectedSite = selectedSiteId != null;

  const loadReport = async () => {
    if (!hasSelectedSite) {
      toast.error("Sélectionnez un site pour afficher les statistiques.");
      return;
    }
    setStatsLoading(true);
    try {
      const params = {
        start_date: moment(startDate).format("YYYY-MM-DD"),
        end_date: moment(endDate).format("YYYY-MM-DD"),
        include_charts: includeCharts,
        include_details: includeDetails,
        site_ids: [selectedSiteId],
      };
      if (selectedTypes.length > 0) {
        params.types = selectedTypes;
      }
      if (selectedStatuses.length > 0) {
        params.statuses = selectedStatuses;
      }
      if (selectedSeverities.length > 0) {
        params.severities = selectedSeverities;
      }

      const data = await api.getCustomReport(params);
      setReportData(data);
    } catch (error) {
      toast.error("Erreur lors du chargement du rapport");
      console.error("Error loading report:", error);
      setReportData(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleShowStats = () => {
    setShowDetailedStats(true);
    loadReport();
  };

  const getIntelligentPayload = () => {
    const p = {
      kind: "custom",
      start_date: moment(startDate).format("YYYY-MM-DD"),
      end_date: moment(endDate).format("YYYY-MM-DD"),
      include_charts: includeCharts,
      include_details: includeDetails,
      site_ids: [selectedSiteId],
    };
    if (selectedTypes.length > 0) p.types = selectedTypes;
    if (selectedStatuses.length > 0) p.statuses = selectedStatuses;
    if (selectedSeverities.length > 0) p.severities = selectedSeverities;
    return p;
  };

  const synthesis = useIntelligentReportGeneration(getIntelligentPayload, {
    disabled: !hasSelectedSite,
    isCompact: true,
  });

  const handleDownloadPDF = async () => {
    if (!hasSelectedSite) {
      toast.error("Sélectionnez un site pour générer le PDF.");
      return;
    }
    setDownloading(true);
    try {
      const params = {
        start_date: moment(startDate).format("YYYY-MM-DD"),
        end_date: moment(endDate).format("YYYY-MM-DD"),
        include_charts: includeCharts,
        include_details: includeDetails,
        site_ids: [selectedSiteId],
      };
      if (selectedTypes.length > 0) {
        params.types = selectedTypes;
      }
      if (selectedStatuses.length > 0) {
        params.statuses = selectedStatuses;
      }
      if (selectedSeverities.length > 0) {
        params.severities = selectedSeverities;
      }

      const blob = await api.downloadCustomReportPDF(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport-personnalise-${moment().format("YYYY-MM-DD")}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Rapport PDF téléchargé avec succès");
    } catch (error) {
      toast.error("Erreur lors du téléchargement du PDF");
      console.error("Error downloading PDF:", error);
    } finally {
      setDownloading(false);
    }
  };

  const siteOptions = sites.map((site) => ({ value: site.id, label: site.name }));

  const synthesisPdfMeta =
    hasSelectedSite && selectedSiteId != null
      ? {
          subtitle: `${siteOptions.find((o) => o.value === selectedSiteId)?.label ?? ""} · ${moment(startDate).format("DD/MM/YYYY")} – ${moment(endDate).format("DD/MM/YYYY")}`,
          filename: `synthese-ia-${moment().format("YYYY-MM-DD")}`,
        }
      : undefined;

  const getTypeLabel = (type) => {
    const labels = {
      positive: "Positive",
      dangerous: "Dangereuse",
      near_miss: "Presque accident",
      non_conformity: "Non-conformité",
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: "Brouillon",
      submitted: "Soumis",
      received: "Reçue",
      in_analysis: "En analyse",
      corrective_action_requested: "Action requise",
      action_completed: "Action réalisée",
      verified: "Vérifiée",
      in_action: "En action",
      closed: "Clôturé",
      rejected: "Rejeté",
    };
    return labels[status] || status;
  };

  const getSeverityLabel = (severity) => {
    const labels = {
      low: "Faible",
      medium: "Moyenne",
      high: "Élevée",
    };
    return labels[severity] || severity;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: "success",
      medium: "warning",
      high: "danger",
    };
    return colors[severity] || "secondary";
  };

  return (
    <React.Fragment>
      <Head title="Rapports Personnalisés" />
      <Content>
        <BlockHead size="sm">
          <BlockHeadContent>
            <BlockTitle page>Rapports Personnalisés</BlockTitle>
            <BlockDes className="text-soft">
              <p>
                Sélectionnez d&apos;abord un <strong>site</strong> (obligatoire) : chaque rapport et la synthèse IA
                portent sur <strong>ce site uniquement</strong>. Les autres filtres restent optionnels.
              </p>
            </BlockDes>
          </BlockHeadContent>
        </BlockHead>

        {/* Filtres */}
        <Block>
          <PreviewAltCard>
            <div className="nk-block-head">
              <h6 className="title">Filtres de rapport</h6>
            </div>
            <Row className="g-gs">
              <Col md="6" lg="3">
                <div className="form-group">
                  <label className="form-label">
                    Site <span className="text-danger">*</span>
                  </label>
                  <RSelect
                    options={siteOptions}
                    value={siteOptions.find((opt) => opt.value === selectedSiteId) ?? null}
                    onChange={(opt) => setSelectedSiteId(opt ? opt.value : null)}
                    placeholder="Choisir un site"
                    isClearable
                  />
                </div>
              </Col>
              <Col md="6" lg="3">
                <div className="form-group">
                  <label className="form-label">Date de début</label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    placeholderText="Date de début"
                  />
                </div>
              </Col>
              <Col md="6" lg="3">
                <div className="form-group">
                  <label className="form-label">Date de fin</label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    placeholderText="Date de fin"
                  />
                </div>
              </Col>
              <Col md="6" lg="3">
                <div className="form-group">
                  <label className="form-label">Types (optionnel)</label>
                  <RSelect
                    options={typeOptions}
                    value={typeOptions.filter((opt) => selectedTypes.includes(opt.value))}
                    onChange={(options) =>
                      setSelectedTypes(options ? options.map((opt) => opt.value) : [])
                    }
                    isMulti
                    placeholder="Tous les types"
                  />
                </div>
              </Col>
              <Col md="6" lg="3">
                <div className="form-group">
                  <label className="form-label">Statuts (optionnel)</label>
                  <RSelect
                    options={statusOptions}
                    value={statusOptions.filter((opt) => selectedStatuses.includes(opt.value))}
                    onChange={(options) =>
                      setSelectedStatuses(options ? options.map((opt) => opt.value) : [])
                    }
                    isMulti
                    placeholder="Tous les statuts"
                  />
                </div>
              </Col>
              <Col md="6" lg="3">
                <div className="form-group">
                  <label className="form-label">Gravités (optionnel)</label>
                  <RSelect
                    options={severityOptions}
                    value={severityOptions.filter((opt) => selectedSeverities.includes(opt.value))}
                    onChange={(options) =>
                      setSelectedSeverities(options ? options.map((opt) => opt.value) : [])
                    }
                    isMulti
                    placeholder="Toutes les gravités"
                  />
                </div>
              </Col>
            </Row>

            <div className="border-top pt-4 mt-3 pb-2">
              <div className="d-flex flex-wrap align-items-start gap-2 mb-3">
                <Button
                  color="secondary"
                  outline
                  onClick={handleDownloadPDF}
                  disabled={downloading || !hasSelectedSite}
                  style={{ marginBottom: "12px" }}
                >
                  {downloading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Téléchargement…
                    </>
                  ) : (
                    <>
                      <Icon name="download" className="me-1" />
                      Télécharger le PDF
                    </>
                  )}
                </Button>
                <Button
                  color="light"
                  outline
                  onClick={handleShowStats}
                  disabled={statsLoading || !hasSelectedSite}
                  style={{ marginBottom: "12px" }}
                >
                  {statsLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Chargement…
                    </>
                  ) : (
                    <>
                      <Icon name="report" className="me-1" />
                      Afficher les statistiques
                    </>
                  )}
                </Button>
                <Button
                  color="primary"
                  onClick={synthesis.run}
                  disabled={synthesis.loading || !hasSelectedSite}
                  style={{ marginBottom: "12px" }}
                >
                  {synthesis.loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Génération…
                    </>
                  ) : (
                    <>
                      <Icon name="file-text" className="me-1" />
                      Générer la synthèse IA
                    </>
                  )}
                </Button>
              </div>
              {!hasSelectedSite && (
                <p className="text-soft small mt-2 mb-0">
                  Sélectionnez un site pour activer le PDF, les statistiques et la synthèse IA.
                </p>
              )}
              {synthesis.intelligent && (
                <div className="border-top pt-4 mt-3">
                  <div className="nk-block-head mb-3">
                    <h6 className="title">Synthèse IA</h6>
                  </div>
                  <IntelligentReportSynthesisContent
                    intelligent={synthesis.intelligent}
                    pdfMeta={synthesisPdfMeta}
                  />
                </div>
              )}
            </div>
          </PreviewAltCard>
        </Block>

        {statsLoading && showDetailedStats && (
          <Block>
            <div className="text-center p-4">
              <Spinner color="primary" />
              <p className="mt-2 text-soft">Chargement des statistiques…</p>
            </div>
          </Block>
        )}

        {showDetailedStats && !statsLoading && reportData && reportData.overview?.total === 0 && (
          <Block>
            <PreviewAltCard>
              <p className="mb-0 text-soft">
                Aucune observation ne correspond à ces critères sur la période sélectionnée.
              </p>
            </PreviewAltCard>
          </Block>
        )}

        {showDetailedStats && !statsLoading && reportData && reportData.overview?.total > 0 && (
          <>
            {/* Vue d'ensemble */}
            <Block>
              <Row className="g-gs">
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Total Observations</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">{reportData.overview.total}</span>
                      </div>
                      <div className="card-stats">
                        <span className="text-soft">
                          {reportData.overview.positive} positives • {reportData.overview.negative}{" "}
                          négatives
                        </span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Taux de clôture</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">{reportData.overview.closure_rate}%</span>
                      </div>
                      <div className="card-stats">
                        <span className="text-soft">{reportData.overview.closed} clôturées</span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Période</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">
                          {moment(reportData.filters.start_date).format("DD/MM/YYYY")} -{" "}
                          {moment(reportData.filters.end_date).format("DD/MM/YYYY")}
                        </span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Filtres appliqués</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">
                          {[
                            reportData.filters.site_ids?.length > 0
                              ? `${reportData.filters.site_ids.length} site(s)`
                              : null,
                            reportData.filters.types?.length > 0
                              ? `${reportData.filters.types.length} type(s)`
                              : null,
                            reportData.filters.statuses?.length > 0
                              ? `${reportData.filters.statuses.length} statut(s)`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(", ") || "Aucun"}
                        </span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
              </Row>
            </Block>

            {/* Répartition par type */}
            {reportData.by_type && reportData.by_type.length > 0 && (
              <Block>
                <Row className="g-gs">
                  <Col lg="6">
                    <PreviewAltCard>
                      <div className="nk-block-head">
                        <h6 className="title">Répartition par type</h6>
                      </div>
                      <div className="nk-tb-list nk-tb-ulog">
                        <div className="nk-tb-item nk-tb-head">
                          <div className="nk-tb-col">
                            <span>Type</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>Nombre</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>Pourcentage</span>
                          </div>
                        </div>
                        {reportData.by_type.map((item, index) => (
                          <div className="nk-tb-item" key={index}>
                            <div className="nk-tb-col">
                              <span>{getTypeLabel(item.type)}</span>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>{item.count}</span>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>
                                {reportData.overview.total > 0
                                  ? ((item.count / reportData.overview.total) * 100).toFixed(1)
                                  : 0}
                                %
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </PreviewAltCard>
                  </Col>
                  {reportData.by_status && reportData.by_status.length > 0 && (
                    <Col lg="6">
                      <PreviewAltCard>
                        <div className="nk-block-head">
                          <h6 className="title">Répartition par statut</h6>
                        </div>
                        <div className="nk-tb-list nk-tb-ulog">
                          <div className="nk-tb-item nk-tb-head">
                            <div className="nk-tb-col">
                              <span>Statut</span>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>Nombre</span>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>Pourcentage</span>
                            </div>
                          </div>
                          {reportData.by_status.map((item, index) => (
                            <div className="nk-tb-item" key={index}>
                              <div className="nk-tb-col">
                                <span>{getStatusLabel(item.status)}</span>
                              </div>
                              <div className="nk-tb-col tb-col-sm">
                                <span>{item.count}</span>
                              </div>
                              <div className="nk-tb-col tb-col-sm">
                                <span>
                                  {reportData.overview.total > 0
                                    ? ((item.count / reportData.overview.total) * 100).toFixed(1)
                                    : 0}
                                  %
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </PreviewAltCard>
                    </Col>
                  )}
                </Row>
              </Block>
            )}

            {/* Répartition par gravité */}
            {reportData.by_severity && reportData.by_severity.length > 0 && (
              <Block>
                <Row className="g-gs">
                  <Col lg="6">
                    <PreviewAltCard>
                      <div className="nk-block-head">
                        <h6 className="title">Répartition par gravité</h6>
                      </div>
                      <div className="nk-tb-list nk-tb-ulog">
                        <div className="nk-tb-item nk-tb-head">
                          <div className="nk-tb-col">
                            <span>Gravité</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>Nombre</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>Pourcentage</span>
                          </div>
                        </div>
                        {reportData.by_severity.map((item, index) => (
                          <div className="nk-tb-item" key={index}>
                            <div className="nk-tb-col">
                              <Badge color={getSeverityColor(item.severity)}>
                                {getSeverityLabel(item.severity)}
                              </Badge>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>{item.count}</span>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>
                                {reportData.overview.total > 0
                                  ? ((item.count / reportData.overview.total) * 100).toFixed(1)
                                  : 0}
                                %
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </PreviewAltCard>
                  </Col>
                </Row>
              </Block>
            )}

            {/* Détails des observations */}
            {reportData.observations && reportData.observations.length > 0 && (
              <Block>
                <Row className="g-gs">
                  <Col lg="12">
                    <PreviewAltCard>
                      <div className="nk-block-head">
                        <h6 className="title">Détails des observations (100 premières)</h6>
                      </div>
                      <div className="nk-tb-list nk-tb-ulog">
                        <div className="nk-tb-item nk-tb-head">
                          <div className="nk-tb-col tb-col-sm">
                            <span>ID</span>
                          </div>
                          <div className="nk-tb-col">
                            <span>Titre</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>Type</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>Statut</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>Gravité</span>
                          </div>
                          <div className="nk-tb-col">
                            <span>Créateur</span>
                          </div>
                          <div className="nk-tb-col">
                            <span>Site</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>Date</span>
                          </div>
                        </div>
                        {reportData.observations.map((obs, index) => (
                          <div className="nk-tb-item" key={index}>
                            <div className="nk-tb-col tb-col-sm">
                              <span>#{obs.id}</span>
                            </div>
                            <div className="nk-tb-col">
                              <span>{obs.title}</span>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>{getTypeLabel(obs.type)}</span>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>{getStatusLabel(obs.status)}</span>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              {obs.severity ? (
                                <Badge color={getSeverityColor(obs.severity)}>
                                  {getSeverityLabel(obs.severity)}
                                </Badge>
                              ) : (
                                <span>N/A</span>
                              )}
                            </div>
                            <div className="nk-tb-col">
                              <span>{obs.creator || "N/A"}</span>
                            </div>
                            <div className="nk-tb-col">
                              <span>{obs.site || "N/A"}</span>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>{moment(obs.created_at).format("DD/MM/YYYY")}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </PreviewAltCard>
                  </Col>
                </Row>
              </Block>
            )}
          </>
        )}

        {showDetailedStats && !statsLoading && !reportData && (
          <Block>
            <PreviewAltCard>
              <p className="mb-0 text-soft">Impossible de charger les statistiques. Réessayez.</p>
            </PreviewAltCard>
          </Block>
        )}
      </Content>
    </React.Fragment>
  );
};

export default CustomReportsPage;

